import { apiClient, Container } from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useState } from "react";
import type { Subscription } from "../../api/subscription";
import type { Ticket } from "../../api/Ticket";
import widgetApi from "../../api/widgetApi";
import type { CompanyWithAvailableSubscriptions } from "../../types/Company";
import type Phone from "../../types/Phone";
import type { UserData } from "../auth/userData";
import PhoneForm from "../order-form/PhoneForm";
import VerificationForm from "../order-form/VerificationForm";
import SubscriptionInfoSummary from "../wrapper/SubscriptionInfoSummary";
import HousingCooperativeInfo from "./HousingCooperativeInfo";
import "./LoginForm.scss";
import Faq from "../wrapper/Faq";

interface Props {
	companies: CompanyWithAvailableSubscriptions[];
	onLoginComplete: (
		userData: UserData,
		company: CompanyWithAvailableSubscriptions,
		subscriptions: Subscription[],
		tickets: Ticket[],
	) => Promise<void>;
	onNeedCompanySelection: (
		userData: UserData,
		subscriptions: Subscription[],
		tickets: Ticket[],
	) => void;
	handleInitError: (error: Error) => void;
	token: string;
}

interface LoginStateInit {
	type: "1-login-state-init";
}

interface LoginStatePhoneSupplied {
	type: "2-login-state-phone-supplied";
	phone: Phone;
}

type LoginState = LoginStateInit | LoginStatePhoneSupplied;

const LoginForm: React.FC<Props> = ({
	onLoginComplete,
	onNeedCompanySelection,
	token,
	companies,
	handleInitError,
}) => {
	const [formState, setFormState] = useState<LoginState>({
		type: "1-login-state-init",
	});

	const showPublicInfo = companies.find(
		(x) => x.subTypeId === "HOUSING_COOPERATIVE_AGREEMENT",
	);

	const companyAsTravelSubscription = companies.some(
		(x) => x.hasTravelSubscription,
	);

	const onPhoneVerified = async (phone: Phone, verificationCode: string) => {
		const userData: UserData = {
			phone: phone.nationalNumber,
			phoneCountryCode: phone.phoneCountryCode,
			phoneVerificationCode: verificationCode,
		};

		const [subscriptionsResponse, ticketsResponse] = await Promise.all([
			apiClient.request(widgetApi.subscriptions.getForUser(token, userData)),
			apiClient.request(widgetApi.tickets.getAll(token, userData)),
		]);

		if (subscriptionsResponse.error) {
			handleInitError(subscriptionsResponse.error);
			return;
		}
		if (ticketsResponse.error) {
			handleInitError(ticketsResponse.error);
			return;
		}

		const existingAgreementId =
			subscriptionsResponse.result.matches[0]?.agreementId ||
			ticketsResponse.result.matches[0]?.agreementNumber;
		const existingCompany = companies.find(
			(x) => x.agreementId === existingAgreementId,
		);

		if (companies.length === 1) {
			await onLoginComplete(
				userData,
				companies[0],
				subscriptionsResponse.result.matches,
				ticketsResponse.result.matches,
			);
		} else if (existingCompany) {
			await onLoginComplete(
				userData,
				existingCompany,
				subscriptionsResponse.result.matches,
				ticketsResponse.result.matches,
			);
		} else {
			onNeedCompanySelection(
				userData,
				subscriptionsResponse.result.matches,
				ticketsResponse.result.matches,
			);
		}
	};

	const showAdditionalInfo = () => {
		const isHousingCooperative = companies.some(
			(x) => x.subTypeId === "HOUSING_COOPERATIVE_AGREEMENT",
		);

		if (companyAsTravelSubscription) {
			return (
				<div className="additional-info">
					<SubscriptionInfoSummary
						hasTravelSubscription={true}
						isHousingCooperative={isHousingCooperative}
						title={
							<h3>
								{isHousingCooperative
									? "Reiseabonnement for borettslag"
									: "Reiseabonnement for bedrifter"}
							</h3>
						}
						className="accordion-border"
					/>
					<h3 style={{ marginTop: "4rem" }}>Vanlige spørsmål</h3>
					<Faq />
				</div>
			);
		}

		return <HousingCooperativeInfo />;
	};

	return (
		<Container width="s" className="components-login-login-form">
			<PhoneForm
				token={token}
				codeSent={(phone) => {
					setFormState({ type: "2-login-state-phone-supplied", phone });
				}}
				active={formState.type === "1-login-state-init"}
			/>
			{showPublicInfo &&
				formState.type === "1-login-state-init" &&
				showAdditionalInfo()}
			{formState.type !== "1-login-state-init" && (
				<div style={{ marginTop: "1rem" }}>
					<VerificationForm
						token={token}
						phone={formState.phone}
						active={formState.type === "2-login-state-phone-supplied"}
						phoneValidated={(verificationCode) => {
							onPhoneVerified(formState.phone, verificationCode);
						}}
					/>
				</div>
			)}
		</Container>
	);
};

export default LoginForm;
